import { Button, Container, Element } from '@playcanvas/pcui';

import { Events } from '../events';
import { ShortcutManager } from '../shortcut-manager';
import { localize } from './localization';
import { ModeToggle } from './mode-toggle';
import { Tooltips } from './tooltips';

// Quick toggles across the top of the viewport for what is flipped most while editing. Grid
// and Bound moved here from View Options; the centers/rings switch keeps its own component.
class ViewportControls extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'viewport-controls'
        };

        super(args);

        // the strip floats over the canvas; keep its input away from the camera controls
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const syncs: (() => void)[] = [];

        const createToggle = (text: string, stateName: string, setEventName: string, tooltip: string) => {
            const button = new Button({
                class: 'viewport-toggle',
                text
            });

            const reflect = (on: boolean) => {
                button.class[on ? 'add' : 'remove']('active');
                button.dom.setAttribute('aria-pressed', String(on));
            };

            events.on(stateName, reflect);
            syncs.push(() => reflect(!!events.invoke(stateName)));

            button.on('click', () => {
                events.fire(setEventName, !events.invoke(stateName));
            });

            tooltips.register(button, tooltip, 'bottom');
            return button;
        };

        const shortcutManager: ShortcutManager = events.invoke('shortcutManager');
        const gridShortcut = shortcutManager.formatShortcut('grid.toggleVisible');
        const gridTooltip = localize('panel.view-options.show-grid');

        const grid = createToggle(
            localize('viewport.grid'),
            'grid.visible',
            'grid.setVisible',
            gridShortcut ? `${gridTooltip} ( ${gridShortcut} )` : gridTooltip
        );

        const bound = createToggle(
            localize('viewport.bound'),
            'camera.bound',
            'camera.setBound',
            localize('panel.view-options.show-bound')
        );

        this.append(new ModeToggle(events, tooltips));
        this.append(new Element({ class: 'viewport-separator' }));
        this.append(grid);
        this.append(bound);

        // The editor registers grid.visible and camera.bound after the UI is built, and never
        // broadcasts camera.bound at startup, so read both once the first frame renders.
        let synced = false;
        events.on('prerender', () => {
            if (!synced) {
                synced = true;
                syncs.forEach(sync => sync());
            }
        });
    }
}

export { ViewportControls };
